import { Divider, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { InlineInput } from "components/FormControls/InlineInput/InlineInput";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as ChevronUpIcon } from "components/Icons/chevron-up.svg";
import { ReactComponent as Copy02Icon } from "components/Icons/copy-02.svg";
import { ReactComponent as Trash02Icon } from "components/Icons/trash-02.svg";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import {
  CampaignCondition,
  CampaignConditionNewFormat,
  CampaignTriggersUnion,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import { Conditions } from "./Conditions/Conditions";
import { Effects } from "./Effects/Effects";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    wrapper: {
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${theme.gray200}`,
      borderRadius: "8px",
      overflow: "visible",
      margin: theme.spacing(0, 0, 8, 0),
    },
    topFunctions: {
      alignItems: "center",
      backgroundColor: theme.gray50,
      borderBottom: ({ isDetailsOpen }: { isDetailsOpen: boolean }) =>
        isDetailsOpen ? `1px solid ${theme.gray100}` : "none",
      borderRadius: ({ isDetailsOpen }: { isDetailsOpen: boolean }) =>
        isDetailsOpen ? theme.spacing(4, 4, 0, 0) : theme.spacing(4),
      display: "flex",
      minHeight: "90px",
      justifyContent: "space-between",
      padding: theme.spacing(6, 8),
      width: "100%",
    },
    ruleHeader: {
      maxWidth: "70%",
      gap: theme.spacing(2.5),
      width: "100%",
    },
    icon: {
      color: theme.gray500,
      cursor: "pointer",
      margin: theme.spacing(0, 2),
    },
    iconDropdown: {
      transition: "0.2s",
      transform: ({ isDetailsOpen }: { isDetailsOpen: boolean }) =>
        isDetailsOpen ? "rotate(0)" : "rotate(180deg)",
    },
    actionWrapper: {
      display: "flex",
      alignItems: "center",
    },
    contentWrapper: {
      display: ({ isDetailsOpen }: { isDetailsOpen: boolean }) =>
        isDetailsOpen ? "flex" : "none",
      alignItems: "center",
      background: theme.white50,
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
    divider: {
      borderBottom: `1px solid ${theme.gray200}`,
    },
    buttonAdd: {
      border: `1px solid ${theme.gray200}`,
      borderRadius: "8px",
      color: theme.gray500,
      padding: theme.spacing(4, 6),
    },
    errorIcon: {
      color: theme.red500,
      margin: theme.spacing(0, 2),
      height: "28px",
      width: "28px",
    },
    borderRadius: {
      borderRadius: "0 0 8px 8px",
    },
    tooltip: {
      display: "flex",
      flexDirection: "column",
    },
    ruleTitle: {
      fontWeight: 600,
      color: theme.primary800,
      fontSize: 14,
    },
    ruleDesc: {
      fontWeight: 400,
      color: theme.primary800,
      lineHeight: "24px",
      fontSize: 12,
    },
    tooltipTitle: {
      fontWeight: 600,
      color: theme.primary800,
      marginBottom: theme.spacing(2),
      fontSize: 14,
    },
    tooltipDescription: {
      fontWeight: 400,
      color: theme.primary800,
      lineHeight: "24px",
      fontSize: 14,
    },
  }),
);

export type RuleError = {
  conditions?: { [key: number]: string };
  effects?: { [key: number]: string };
};

interface Props {
  index: number;
  addRule: (rule: any) => void;
  removeRule: (index: number) => void;
  rule: any;
  conditions: (
    | CampaignCondition
    | SelectedSchemaCondition
    | CampaignConditionNewFormat
  )[];
  error?: RuleError;
  selectedSchemaConditions?: SelectedSchemaCondition[];
  isMaxRulesCount: boolean;
  trigger?: CampaignTriggersUnion;
  transactionItemsFilters?: TransactionItem[];
}

export const Rule = ({
  index,
  addRule,
  removeRule,
  rule,
  conditions,
  error,
  selectedSchemaConditions,
  isMaxRulesCount,
  trigger,
  transactionItemsFilters,
}: Props) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const classes = useStyles({ isDetailsOpen });
  const { message } = useMessage();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isEditingRuleNaming, setIsEditingRuleNaming] = useState(false);

  const areAnyConditionsWithError =
    error?.conditions &&
    Object.keys(error.conditions).length > 0 &&
    Object.values(error.conditions).every((item) => !!item);

  const areAnyEffectsWithError =
    error?.effects &&
    Object.keys(error.effects).length > 0 &&
    Object.values(error.effects).every((item) => !!item);

  const isRuleWithError = areAnyConditionsWithError || areAnyEffectsWithError;
  const shouldDisplayTooltipNaming = rule?.name || rule?.description;

  return (
    <section
      className={classes.wrapper}
      data-test-id={`rules-${index}-container`}
    >
      <div className={classes.topFunctions}>
        <Tooltip
          customHandler
          text={
            shouldDisplayTooltipNaming ? (
              <div className={classes.tooltip}>
                <Typography className={classes.tooltipTitle}>
                  {rule?.name || `${message({ id: "rule" })} ${index + 1}`}
                </Typography>
                <Typography className={classes.tooltipDescription}>
                  {rule?.description || message({ id: "description" })}
                </Typography>
              </div>
            ) : (
              ""
            )
          }
          isOpen={isTooltipOpen && !isEditingRuleNaming}
          onClose={() => setIsTooltipOpen(false)}
          onOpen={() => setIsTooltipOpen(true)}
        >
          <div className={classes.ruleHeader}>
            <InlineInput
              name={`rules[${index}].name`}
              placeholder={`${message({ id: "rule" })} ${index + 1}`}
              options={{
                disablePreviewTooltip: true,
              }}
              onEditModeOpen={() => setIsEditingRuleNaming(true)}
              onEditModeClose={() => {
                setIsTooltipOpen(false);
                setIsEditingRuleNaming(false);
              }}
              className={classes.ruleTitle}
              autoFocus
            />
            <InlineInput
              name={`rules[${index}].description`}
              placeholder={message({ id: "description" })}
              options={{
                disablePreviewTooltip: true,
              }}
              onEditModeOpen={() => setIsEditingRuleNaming(true)}
              onEditModeClose={() => {
                setIsTooltipOpen(false);
                setIsEditingRuleNaming(false);
              }}
              className={classes.ruleDesc}
              autoFocus
            />
          </div>
        </Tooltip>

        <div className={classes.actionWrapper}>
          {isRuleWithError && !isDetailsOpen && (
            <AlertTriangleIcon
              data-test-id={`rule-rule-${index}-error`}
              className={classes.errorIcon}
              fontSize="medium"
            />
          )}
          {!isMaxRulesCount && (
            <Copy02Icon
              onClick={() => addRule(rule)}
              className={classes.icon}
              fontSize="small"
              data-test-id={`rule-${index}-duplicate`}
            />
          )}
          <Trash02Icon
            onClick={() => setIsDeleteModalOpen(true)}
            className={classes.icon}
            fontSize="medium"
            data-test-id={`rule-${index}-delete`}
          />
          <ChevronUpIcon
            onClick={() => setIsDetailsOpen(!isDetailsOpen)}
            className={`${classes.icon} ${classes.iconDropdown}`}
            fontSize="small"
            data-test-id={`rule-${index}-open-details`}
          />
        </div>
      </div>
      <div className={classes.contentWrapper}>
        <Conditions
          ruleIndex={index}
          conditions={conditions}
          rule={rule}
          error={error?.conditions}
          trigger={trigger}
          transactionItemsFilters={transactionItemsFilters}
        />
      </div>
      {!rule?.conditions?.at(-1)?.isEditing && isDetailsOpen && (
        <Divider className={classes.divider} />
      )}
      <div className={`${classes.contentWrapper} ${classes.borderRadius}`}>
        <Effects
          ruleIndex={index}
          error={error?.effects}
          selectedSchemaConditions={selectedSchemaConditions}
        />
      </div>
      <ConfirmationModal
        action={() => {
          setIsDeleteModalOpen(false);
          removeRule(index);
        }}
        close={() => {
          setIsDeleteModalOpen(false);
        }}
        cancelText={message({ id: "cancel" })}
        header="deleteRule"
        info="deleteRuleConfirm"
        open={isDeleteModalOpen}
        submitText="deleteRule"
      />
    </section>
  );
};
